import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, X, Pencil } from "lucide-react";
import axios from "axios";
import Cookies from 'js-cookie';
import httpClient from "../../../api/httpClient";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { fetchCalendarByRef } from "../Appointments/TabContent/api/AppointmentsApi";
import DatezonePicker from "../../../components/ModuleCommon/StickyHeader/DatazonePicker";
import { getOfficeCode, fetchYardSettings, yardSettingsWriteOfficeCodesRequest } from "./api";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];
const getLocKey = (loc) => loc?.uid ?? loc?.id ?? loc?.locationRef;

/** Shared with YardOverviewPanel.js (My Yard) so switching tabs keeps the same Location/Warehouse selected. */
const YARD_VIEW_STORAGE_KEY = "yms_yard_view_selection";

function readYardViewSelection() {
  try {
    const raw = sessionStorage.getItem(YARD_VIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeYardViewSelection(selection) {
  try {
    sessionStorage.setItem(YARD_VIEW_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // ignore quota errors
  }
}

/** GET/PUT /api/yard-settings/{segment} — `displayName` is the selected warehouse label (StickyHeader / dropdown), URL-encoded. */
const encodeYardSettingsKey = (displayName) =>
  encodeURIComponent(String(displayName ?? "").trim());

const capitalizeFirst = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

const transformCalendarToHours = (calData) => {
  const regularHours = {};
  const holidays = [];
  for (const evt of calData?.event || []) {
    const recurType = (evt.event_recurring_type || evt.recurringType || "").toUpperCase();
    const statusType = (evt.event_statue_type || evt.eventStatusType || evt.eventTypeName || "").toUpperCase();
    if (recurType === "WEEKLY" && statusType === "OPEN") {
      const day = capitalizeFirst(evt.dayOfWeek);
      if (day) {
        regularHours[day] = {
          from: evt.startTime?.slice(0, 5) || "09:00",
          to: evt.endTime?.slice(0, 5) || "17:00",
          eventId: evt.eventId,
        };
      }
    }
    if (recurType === "YEARLY" && statusType === "CLOSE") {
      const month = evt.monthOfYear?.toString().padStart(2, "0");
      const day = evt.dayOfMonth?.toString().padStart(2, "0");
      if (month && day) {
        holidays.push({
          date: `${new Date().getFullYear()}-${month}-${day}`,
          reason: evt.eventDescription,
          eventId: evt.eventId,
        });
      }
    }
  }
  return {
    regularHours,
    holidays,
    calendarId: calData?.calendarId,
    calendarRefId: calData?.calendarRefId,
  };
};

// ─── Shared style tokens (matches the app's fluid-spacing / dark-mode Tailwind system) ──
const inputClass =
  "w-full rounded-md border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-acs-dark-bg " +
  "px-fluid-input-x py-fluid-input-y text-fluid-sm text-gray-900 dark:text-acs-dark-text outline-none " +
  "focus:ring-2 focus:ring-acs-primary-navy/30 dark:focus:ring-acs-primary-navy-dark/40";

const inputErrorClass =
  "w-full rounded-md border border-Delete-red bg-white dark:bg-acs-dark-bg " +
  "px-fluid-input-x py-fluid-input-y text-fluid-sm text-gray-900 dark:text-acs-dark-text outline-none " +
  "focus:ring-2 focus:ring-Delete-red/30";

const labelClass = "block text-fluid-xs font-semibold text-gray-700 dark:text-acs-dark-subtext mb-fluid-0.5";

const sectionLabelClass =
  "text-fluid-xs font-semibold text-gray-500 dark:text-acs-dark-subtext uppercase tracking-wide";

const modalOverlayClass =
  "fixed inset-0 z-[999] bg-black bg-opacity-40 flex items-center justify-center px-fluid-4";

const modalCardClass =
  "bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl flex flex-col font-roboto";

const modalHeaderClass =
  "flex items-center justify-between px-fluid-4 py-fluid-3 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0";

const modalTitleClass = "text-fluid-base font-semibold text-gray-900 dark:text-acs-dark-text";

const modalCloseButtonClass =
  "bg-transparent border-none cursor-pointer text-gray-400 dark:text-acs-dark-subtext hover:text-gray-600 dark:hover:text-acs-dark-text p-fluid-0.5 leading-none";

const modalFooterClass =
  "flex justify-end gap-fluid-2 px-fluid-4 py-fluid-3 border-t border-gray-200 dark:border-acs-dark-border flex-shrink-0";

const modalCancelButtonClass =
  "px-fluid-button-x py-fluid-button-y border border-gray-300 dark:border-acs-dark-border rounded-md " +
  "text-fluid-sm text-gray-700 dark:text-acs-dark-text bg-white dark:bg-acs-dark-surface " +
  "hover:bg-gray-100 dark:hover:bg-acs-dark-hover transition-none";

const modalPrimaryButtonClass =
  "px-fluid-button-x py-fluid-button-y rounded-md text-fluid-sm font-semibold text-white " +
  "bg-acs-primary-navy dark:bg-acs-primary-navy-dark hover:bg-[#1E2A74] transition-none";

const modalDangerButtonClass =
  "px-fluid-button-x py-fluid-button-y rounded-md text-fluid-sm font-semibold text-white " +
  "bg-Delete-red hover:bg-red-700 transition-none";

const linkButtonClass =
  "text-fluid-xs font-semibold text-acs-primary-navy dark:text-acs-dark-text bg-transparent border-none " +
  "cursor-pointer p-0 hover:underline";

// ─── Collapsible month group ──────────────────────────────────────────────────
const CollapsibleMonthGroup = ({ month, holidays, onRemove }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-fluid-1">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between bg-[#f0f2fa] dark:bg-acs-dark-hover
          border-none rounded-md px-fluid-2 py-fluid-1 cursor-pointer"
      >
        <span className="text-fluid-xs font-semibold text-acs-primary-navy dark:text-acs-dark-text">{month}</span>
        <span className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-fluid-0.5">
          {holidays.map((h) => (
            <div
              key={h.date}
              className="flex justify-between items-center text-fluid-xs bg-white dark:bg-acs-dark-surface
                border border-gray-200 dark:border-acs-dark-border rounded px-fluid-2 py-fluid-0.5
                text-gray-700 dark:text-acs-dark-subtext mb-fluid-0.5"
            >
              <span>{h.date}</span>
              <button
                onClick={() => { onRemove(h.date); notify.success({ title: "Removed", message: `Holiday ${h.date} removed.` }); }}
                className="bg-transparent border-none cursor-pointer text-gray-400 dark:text-acs-dark-subtext
                  hover:text-gray-600 dark:hover:text-acs-dark-text p-0 text-fluid-base leading-none"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Add Section Modal ────────────────────────────────────────────────────────
const AddSectionModal = ({ onClose, onSave }) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [bayNames, setBayNames] = useState([""]);
  const [bulkCount, setBulkCount] = useState("");

  const addItem = (setter) => setter((prev) => [...prev, ""]);
  const removeItem = (setter, i) => setter((prev) => prev.filter((_, idx) => idx !== i));
  const updateItem = (setter, i, val) => setter((prev) => prev.map((v, idx) => idx === i ? val : v));

  const handleBulkAddBays = () => {
    const n = parseInt(bulkCount, 10);
    if (!n || n <= 0) return;
    setBayNames((prev) => {
      const base = prev.filter((v) => v.trim() !== "");
      const additions = Array.from({ length: n }, (_, i) => `Bay ${base.length + i + 1}`);
      return [...base, ...additions];
    });
    setBulkCount("");
  };

  const handleSave = () => {
    if (!name.trim()) { setNameError("Section name is required."); return; }
    onSave({
      name: name.trim(),
      bayNames,
      bays: bayNames.length,
      dockNames: [],
      docks: 0,
    });
  };

  return (
    <div className={modalOverlayClass}>
      <div className={`${modalCardClass} w-[420px] max-h-[80vh]`}>
        <div className={modalHeaderClass}>
          <p className={modalTitleClass}>Add Section</p>
          <button onClick={onClose} className={modalCloseButtonClass}><X size={18} /></button>
        </div>
        <div className="px-fluid-4 pt-fluid-3 pb-fluid-1 overflow-y-auto flex-1">
          <div className="mb-fluid-3">
            <label className={labelClass}>Section Name <span className="text-Delete-red">*</span></label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(""); }}
              placeholder="e.g. Section A"
              className={nameError ? inputErrorClass : inputClass}
            />
            {nameError && <p className="text-fluid-xs text-Delete-red mt-fluid-0.5">{nameError}</p>}
          </div>

          {/* Bays */}
          <div className="mb-fluid-3">
            <label className={labelClass}>Bays</label>

            <div className="flex gap-fluid-1 mb-fluid-2 items-center">
              <input
                type="number"
                min="1"
                value={bulkCount}
                onChange={(e) => setBulkCount(e.target.value)}
                placeholder="e.g. 20"
                className={`${inputClass} w-[90px]`}
              />
              <button
                type="button"
                onClick={handleBulkAddBays}
                disabled={!bulkCount}
                className={`${modalCancelButtonClass} whitespace-nowrap ${bulkCount ? "" : "opacity-40 cursor-not-allowed"}`}
              >
                Populate Bays
              </button>
            </div>

            {bayNames.map((bayName, i) => (
              <div key={i} className="flex gap-fluid-1 mb-fluid-1 items-center">
                <input
                  value={bayName}
                  onChange={(e) => updateItem(setBayNames, i, e.target.value)}
                  placeholder={`Bay ${i + 1} name`}
                  className={inputClass}
                />
                {bayNames.length > 1 && (
                  <button type="button" onClick={() => removeItem(setBayNames, i)}
                    className="bg-transparent border-none cursor-pointer text-gray-400 dark:text-acs-dark-subtext leading-none flex-shrink-0">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => addItem(setBayNames)} className={linkButtonClass}>
              + Add Bay
            </button>
          </div>
        </div>
        <div className={modalFooterClass}>
          <button onClick={onClose} className={modalCancelButtonClass}>Cancel</button>
          <button onClick={handleSave} className={modalPrimaryButtonClass}>Add Section</button>
        </div>
      </div>
    </div>
  );
};

// ─── Edit Section Modal ───────────────────────────────────────────────────────
const EditSectionModal = ({ section, onClose, onSave }) => {
  const [name, setName] = useState(section.name);
  const [nameError, setNameError] = useState("");
  const [bayNames, setBayNames] = useState(
    section.bayNames ?? Array.from({ length: section.bays ?? 1 }, (_, i) => `Bay ${i + 1}`)
  );
  const [bulkCount, setBulkCount] = useState("");

  const addItem = (setter) => setter((prev) => [...prev, ""]);
  const removeItem = (setter, i) => setter((prev) => prev.filter((_, idx) => idx !== i));
  const updateItem = (setter, i, val) => setter((prev) => prev.map((v, idx) => idx === i ? val : v));

  const handleBulkAddBays = () => {
    const n = parseInt(bulkCount, 10);
    if (!n || n <= 0) return;
    setBayNames((prev) => {
      const base = prev.filter((v) => v.trim() !== "");
      const additions = Array.from({ length: n }, (_, i) => `Bay ${base.length + i + 1}`);
      return [...base, ...additions];
    });
    setBulkCount("");
  };

  const handleSave = () => {
    if (!name.trim()) { setNameError("Section name is required."); return; }
    onSave({
      name: name.trim(),
      bayNames,
      bays: bayNames.length,
      dockNames: [],
      docks: 0,
    });
  };

  return (
    <div className={modalOverlayClass}>
      <div className={`${modalCardClass} w-[420px] max-h-[80vh]`}>
        <div className={modalHeaderClass}>
          <p className={modalTitleClass}>Edit Section</p>
          <button onClick={onClose} className={modalCloseButtonClass}>
            <X size={18} />
          </button>
        </div>
        <div className="px-fluid-4 pt-fluid-3 pb-fluid-1 overflow-y-auto flex-1">
          <div className="mb-fluid-3">
            <label className={labelClass}>Section Name <span className="text-Delete-red">*</span></label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(""); }}
              className={nameError ? inputErrorClass : inputClass}
            />
            {nameError && <p className="text-fluid-xs text-Delete-red mt-fluid-0.5">{nameError}</p>}
          </div>
          {/* Bays */}
          <div className="mb-fluid-3">
            <label className={labelClass}>Bays</label>

            <div className="flex gap-fluid-1 mb-fluid-2 items-center">
              <input
                type="number"
                min="1"
                value={bulkCount}
                onChange={(e) => setBulkCount(e.target.value)}
                placeholder="e.g. 20"
                className={`${inputClass} w-[90px]`}
              />
              <button
                type="button"
                onClick={handleBulkAddBays}
                disabled={!bulkCount}
                className={`${modalCancelButtonClass} whitespace-nowrap ${bulkCount ? "" : "opacity-40 cursor-not-allowed"}`}
              >
                Populate Bays
              </button>
            </div>

            {bayNames.map((bayName, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] gap-fluid-1 items-center mb-fluid-1">
                <div>
                  <p className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext mb-fluid-0.25">
                    Bay {i + 1}
                  </p>
                  <input
                    value={bayName}
                    onChange={(e) => updateItem(setBayNames, i, e.target.value)}
                    className={inputClass}
                  />
                </div>
                {bayNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(setBayNames, i)}
                    className="bg-transparent border-none cursor-pointer text-Delete-red"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => addItem(setBayNames)} className={linkButtonClass}>
              + Add Bay
            </button>
          </div>
        </div>
        <div className={modalFooterClass}>
          <button onClick={onClose} className={modalCancelButtonClass}>Cancel</button>
          <button onClick={handleSave} className={modalPrimaryButtonClass}>Save</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const YardSettingsTab = ({
  locations: locationsFromParent,
  isLoadingLocations,
  onClose,
  onYardSettingsSaved,
}) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedWarehouseIndex, setSelectedWarehouseIndex] = useState(null);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const [regularHours, setRegularHours] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [holidayInput, setHolidayInput] = useState("");
  const [sameAsWarehouse, setSameAsWarehouse] = useState(false);
  const [calendarRefId, setCalendarRefId] = useState(null);
  const [sections, setSections] = useState([]);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);

  const [editingSection, setEditingSection] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [savedYardHours, setSavedYardHours] = useState({});
  const DEFAULT_DISPATCH_SETTINGS = {
    checkinAutoCreateEnabled: false,
    appointmentLeadMinutes: 30,
    autoAssignEnabled: false,
  };
  const [dispatchSettings, setDispatchSettings] = useState(DEFAULT_DISPATCH_SETTINGS);

  const handleEditSection = (updated) => {
    setSections((prev) => prev.map((s) => s.id === editingSection.id ? { ...s, ...updated } : s));
    setEditingSection(null);
  };

  const [infoData, setInfoData] = useState({
    Location: { label: "Location", type: "dropdown", value: "Select Location", options: [], isPlaceholder: true },
    Warehouse: { label: "Warehouse", type: "dropdown", value: "Select Warehouse", options: [], isPlaceholder: true },
  });

  useEffect(() => {
    if (!locationsFromParent?.length) return;
    setLocations(locationsFromParent.map((loc) => ({ ...loc, warehouses: loc.warehouses ?? [] })));
  }, [locationsFromParent]);

  const selectedLocation = locations.find((loc) => getLocKey(loc) === selectedLocationId);
  const selectedWarehouse = selectedLocation?.warehouses?.[selectedWarehouseIndex] ?? null;

  useEffect(() => {
    setInfoData((prev) => ({
      ...prev,
      Location: {
        ...prev.Location,
        value: selectedLocation?.locationName || "Select Location",
        options: locations.map((l) => l.locationName),
        isPlaceholder: !selectedLocation,
      },
      Warehouse: {
        ...prev.Warehouse,
        value: selectedWarehouse?.locationName || "Select Warehouse",
        options: selectedLocation ? (selectedLocation.warehouses ?? []).map((w) => w.locationName) : [],
        isPlaceholder: !selectedWarehouse,
      },
    }));
  }, [locations, selectedLocationId, selectedWarehouseIndex]);

  const loadWarehousesForLocation = useCallback(async (loc) => {
    setIsLoadingWarehouses(true);
    try {
      const whRes = await httpClient.get(
        `/api/locations/children/by-id?parentId=${loc.id}`
      );
      const rawWarehouses = whRes.data ?? [];
      const enrichedWarehouses = await Promise.all(
        rawWarehouses.map(async (warehouse) => {
          let settings = { regularHours: {}, holidays: [], calendarRefId: null };
          try {
            const calRef = warehouse?.calendar?.calendarRefId;
            if (calRef) {
              const calData = await fetchCalendarByRef(calRef);
              settings = transformCalendarToHours(calData);
            }
          } catch { /* leave defaults */ }
          return { ...warehouse, settings };
        })
      );
      setLocations((prev) =>
        prev.map((l) =>
          getLocKey(l) === getLocKey(loc)
            ? { ...l, warehouses: enrichedWarehouses, warehousesLoaded: true }
            : l
        )
      );
      return enrichedWarehouses;
    } catch (err) {
      console.error("❌ Failed to load warehouses:", err);
      // Still mark as loaded so the retry effect below doesn't refire this request forever.
      setLocations((prev) =>
        prev.map((l) => (getLocKey(l) === getLocKey(loc) ? { ...l, warehousesLoaded: true } : l))
      );
      return [];
    } finally {
      setIsLoadingWarehouses(false);
    }
  }, []);

  const handleSameAsWarehouse = async (checked) => {
    setSameAsWarehouse(checked);

    if (!checked) {
      // Restore the yard's own hours when unchecked
      setRegularHours(savedYardHours);
      return;
    }

    const calRef = selectedWarehouse?.calendar?.calendarRefId;
    if (!calRef) {
      notify.error({ title: "No Calendar", message: "This warehouse has no calendar configured." });
      return;
    }

    try {
      const calData = await fetchCalendarByRef(calRef);
      const { regularHours: whHours } = transformCalendarToHours(calData);
      setRegularHours(whHours);
    } catch {
      notify.error({ title: "Error", message: "Failed to load warehouse hours." });
    }
  };

  const applyLocationSelection = useCallback((loc) => {
    setSelectedLocationId(getLocKey(loc));
    setSelectedWarehouseIndex(null);
    setRegularHours({});
    setHasExistingSettings(false);
    setHolidays([]);
    setCalendarRefId(null);
    setSections([]);
    setDispatchSettings(DEFAULT_DISPATCH_SETTINGS);
    setInfoData((prev) => ({
      ...prev,
      Location: { ...prev.Location, value: loc.locationName },
      Warehouse: { ...prev.Warehouse, value: "Select Warehouse", options: [] },
    }));
  }, []);

  const applyWarehouseSelection = useCallback(async (wh, warehouseIndex, loc) => {
    setSelectedWarehouseIndex(warehouseIndex);
    setSameAsWarehouse(false);
    setInfoData((prev) => ({ ...prev, Warehouse: { ...prev.Warehouse, value: wh.locationName } }));
    if (loc) {
      writeYardViewSelection({
        locationId: getLocKey(loc),
        warehouseRef: wh.locationRef,
        warehouseName: wh.locationName,
      });
    }

    setIsLoadingSections(true);
    try {
      const data = await fetchYardSettings(wh.locationName);
      setHasExistingSettings(!data._isNew);
      setRegularHours(data.regularHours ?? {});
      setSavedYardHours(data.regularHours ?? {});
      setHolidays(
        (data.holidays ?? []).map((h) => ({ ...h, deleted: !!h.isDeleted }))
      );
      setCalendarRefId(null);
      setSections(
        (data.sections ?? []).map((s, i) => ({
          id: i + 1,
          name: s.name,
          sectionId: s.sectionId,
          bayNames: s.bayNames ?? [],
          bays: (s.bayNames ?? []).length,
        }))
      );
      setDispatchSettings(data.dispatchSettings ?? DEFAULT_DISPATCH_SETTINGS);
    } catch (err) {
      setHasExistingSettings(false);  // 404 — nothing saved yet, use POST on save
      setRegularHours({});
      setHolidays([]);
      setSections([]);
      setDispatchSettings(DEFAULT_DISPATCH_SETTINGS);
    } finally {
      setIsLoadingSections(false);
    }
  }, []);

  const handleInfoChange = async (field, newValue) => {
    if (field === "Location") {
      const loc = locations.find((l) => l.locationName === newValue);
      if (!loc) return;
      applyLocationSelection(loc);
      await loadWarehousesForLocation(loc);
    }

    if (field === "Warehouse") {
      const wh = selectedLocation?.warehouses?.find((w) => w.locationName === newValue);
      if (!wh) return;
      await applyWarehouseSelection(wh, selectedLocation.warehouses.indexOf(wh), selectedLocation);
    }
  };

  // ─── Confirm Delete Modal ─────────────────────────────────────────────────────
  const ConfirmDeleteModal = ({ onConfirm, onCancel }) => (
    <div className={modalOverlayClass}>
      <div className={`${modalCardClass} w-[360px]`}>
        <div className="px-fluid-4 pt-fluid-3 pb-fluid-1">
          <p className="text-fluid-base font-semibold text-gray-900 dark:text-acs-dark-text mb-fluid-1">
            Remove Section?
          </p>
          <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext">
            This will remove the section. This action cannot be undone.
          </p>
        </div>
        <div className={`${modalFooterClass} mt-fluid-2`}>
          <button onClick={onCancel} className={modalCancelButtonClass}>Cancel</button>
          <button onClick={onConfirm} className={modalDangerButtonClass}>Remove</button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (!selectedLocationId || isLoadingWarehouses) return;
    const loc = locations.find((l) => getLocKey(l) === selectedLocationId);
    if (loc && !loc.warehousesLoaded) {
      loadWarehousesForLocation(loc);
    }
  }, [selectedLocationId, locations, isLoadingWarehouses, loadWarehousesForLocation]);

  // Preselect whatever Location/Warehouse was last picked in My Yard (or here), so switching
  // tabs doesn't force reselecting it every time.
  const [pendingRestoreWarehouseRef, setPendingRestoreWarehouseRef] = useState(null);

  useEffect(() => {
    if (!locations.length || selectedLocationId) return;
    const saved = readYardViewSelection();
    if (!saved?.locationId) return;
    const loc = locations.find((l) => getLocKey(l) === saved.locationId);
    if (!loc) return;
    applyLocationSelection(loc);
    if (saved.warehouseRef) setPendingRestoreWarehouseRef(saved.warehouseRef);
  }, [locations, selectedLocationId, applyLocationSelection]);

  useEffect(() => {
    if (!pendingRestoreWarehouseRef || !selectedLocation?.warehousesLoaded) return;
    const idx = (selectedLocation.warehouses ?? []).findIndex(
      (w) => w.locationRef === pendingRestoreWarehouseRef
    );
    setPendingRestoreWarehouseRef(null);
    if (idx >= 0) {
      applyWarehouseSelection(selectedLocation.warehouses[idx], idx, selectedLocation);
    }
  }, [pendingRestoreWarehouseRef, selectedLocation, applyWarehouseSelection]);

  const handleAddSection = (newSection) => {
    const nextId = Math.max(0, ...sections.map((s) => s.id)) + 1;
    setSections((prev) => [...prev, { id: nextId, ...newSection }]);
    setShowAddSectionModal(false);
  };

  const updateSection = (id, field, val) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: val } : s)));

  const removeSection = (id) =>
    setSections((prev) => prev.filter((s) => s.id !== id));

  const setHourField = (day, field, val) => {
    setSameAsWarehouse(false);
    setRegularHours((prev) => ({ ...prev, [day]: { ...prev[day], [field]: val } }));
  };

  const toggleDay = (day) => {
    setSameAsWarehouse(false);
    if (regularHours[day]) {
      setRegularHours((prev) => { const next = { ...prev }; delete next[day]; return next; });
    } else {
      setRegularHours((prev) => ({ ...prev, [day]: { from: "09:00", to: "17:00" } }));
    }
  };

  const addHoliday = () => {
    if (holidayInput && !holidays.find((h) => h.date === holidayInput)) {
      setHolidays((prev) => [...prev, { date: holidayInput }]);
      notify.success({ title: "Holiday Added", message: `${holidayInput} added as a holiday.` });
      setHolidayInput("");
    }
  };

  const removeHoliday = (date) =>
    setHolidays((prev) =>
      prev.map((h) => h.date === date ? { ...h, deleted: true } : h)
    );

  // Group holidays by month
  const groupedHolidays = holidays
    .filter((h) => !h.deleted)
    .reduce((acc, h) => {
      const monthIdx = parseInt(h.date?.split("-")[1], 10) - 1;
      const monthName = MONTH_NAMES[monthIdx] ?? "Unknown";
      if (!acc[monthName]) acc[monthName] = [];
      acc[monthName].push(h);
      return acc;
    }, {});

  const handleSave = async () => {
    if (!selectedWarehouse) return;
    setIsSaving(true);

    const oc = String(
      selectedWarehouse?.officeCode ||
        selectedLocation?.officeCode ||
        getOfficeCode() ||
        ""
    ).trim();
    const token = Cookies.get("authToken");
    const { params: officeCodeParams, headerFields } = yardSettingsWriteOfficeCodesRequest(oc);
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...headerFields,
    };

    const warehouseDisplayName =
      (infoData.Warehouse?.value &&
      infoData.Warehouse.value !== "Select Warehouse" &&
      !infoData.Warehouse.isPlaceholder
        ? String(infoData.Warehouse.value).trim()
        : "") || (selectedWarehouse.locationName ?? "").trim();

    const body = {
      officeCode: oc,
      warehouseRef: warehouseDisplayName,
      locationRef: selectedLocation?.locationRef ?? selectedLocation?.uid ?? selectedLocation?.id,
      regularHours,
      holidays,
      sections: sections.map((s) => ({
        name: s.name,
        bayNames: s.bayNames ?? [],
        dockNames: [],
      })),
      dispatchSettings,
    };

    try {
      if (hasExistingSettings) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/yard-settings/${encodeYardSettingsKey(warehouseDisplayName)}`,
          body,
          { headers, params: officeCodeParams }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/yard-settings/create`,
          body,
          { headers, params: officeCodeParams }
        );
      }
      setHasExistingSettings(true);
      notify.success({ title: "Saved", message: "Yard settings saved successfully." });
      onYardSettingsSaved?.();
    } catch (err) {
      console.error("❌ Save failed:", err);
      notify.error({ title: "Save Failed", message: "Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col font-roboto">

      {/* StickyHeader */}
      <div className="sticky top-0 z-50 bg-white dark:bg-acs-dark-surface shadow-md w-full">
        <StickyHeader
          mode={{}}
          infoData={infoData}
          onInfoChange={handleInfoChange}
          buttons={[
            {
              label: isSaving ? "Saving..." : "Save",
              type: "primary",
              color: "blue",
              onClick: handleSave,
              disabled: !selectedWarehouse || isSaving,
            },
          ]}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-acs-dark-bg p-fluid-3 space-y-fluid-4">
        {!selectedWarehouse ? (
          <p className="text-fluid-sm text-gray-400 dark:text-acs-dark-subtext">
            Select a location and warehouse to configure yard settings.
          </p>
        ) : (
          <>
            {/* ── Sections & Bays ── */}
            <div>
              <p className={`${sectionLabelClass} mb-fluid-2`}>Sections & Bays</p>

              {isLoadingSections ? (
                <p className="text-fluid-sm text-gray-400 dark:text-acs-dark-subtext">Loading sections...</p>
              ) : (
                <div className="flex gap-fluid-2 flex-wrap items-start">
                  {/* Dashed Add Section card */}
                  <button
                    onClick={() => setShowAddSectionModal(true)}
                    className="w-[140px] h-[110px] border-2 border-dashed border-gray-300 dark:border-acs-dark-border
                      rounded-lg bg-gray-50 dark:bg-acs-dark-surface hover:bg-gray-100 dark:hover:bg-acs-dark-hover
                      flex flex-col items-center justify-center gap-fluid-1 cursor-pointer
                      text-acs-primary-navy dark:text-acs-dark-text"
                  >
                    <Plus size={22} strokeWidth={1.8} />
                    <span className="text-fluid-xs font-medium">Add Section</span>
                  </button>

                  {sections.map((sec) => (
                    <div
                      key={sec.id}
                      className="border border-gray-200 dark:border-acs-dark-border rounded-lg
                        bg-white dark:bg-acs-dark-surface px-fluid-2 py-fluid-2 w-[220px] max-h-[260px]
                        flex flex-col shadow-sm overflow-hidden"
                    >
                      <div className="flex items-center justify-between mb-fluid-1.5">
                        <p className="text-fluid-sm font-semibold text-gray-900 dark:text-acs-dark-text">{sec.name}</p>
                        <div className="flex gap-fluid-0.5">
                          <button
                            onClick={() => setEditingSection(sec)}
                            className="bg-transparent border-none cursor-pointer text-acs-primary-navy dark:text-acs-dark-text p-fluid-0.25 leading-none"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(sec.id)}
                            className="bg-transparent border-none cursor-pointer text-Delete-red p-fluid-0.25 leading-none"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      {/* Bays */}
                      <div className="overflow-y-auto flex-1 pr-fluid-0.5">
                        <p className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext font-semibold mb-fluid-0.5">Bays</p>
                        {(sec.bayNames ?? Array.from({ length: sec.bays || 1 })).map((bay, i) => (
                          <div
                            key={i}
                            className="grid grid-cols-[50px_1fr] items-center gap-fluid-1 mb-fluid-1"
                          >
                            <span className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext">
                              Bay {i + 1}
                            </span>
                            <input
                              value={sec.bayNames?.[i] || ""}
                              readOnly
                              className={`${inputClass} bg-gray-100 dark:bg-acs-dark-hover cursor-default`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <hr className="border-none border-t border-gray-200 dark:border-acs-dark-border" />

            {/* ── Auto-Dispatch ── */}
            <div>
              <p className={`${sectionLabelClass} mb-fluid-2`}>Auto-Dispatch (Hostler Jobs)</p>
              <div className="max-w-[480px] space-y-fluid-3">
                <label className="flex items-start gap-fluid-1.5 text-fluid-sm text-gray-700 dark:text-acs-dark-subtext cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dispatchSettings.checkinAutoCreateEnabled}
                    onChange={(e) =>
                      setDispatchSettings((prev) => ({ ...prev, checkinAutoCreateEnabled: e.target.checked }))
                    }
                    className="w-[15px] h-[15px] mt-fluid-0.25 accent-acs-checkbox dark:accent-acs-checkbox-dark"
                  />
                  <span>
                    Auto-create a Hostler job when a Drop Trailer checks in with a dock already
                    reserved on its appointment
                  </span>
                </label>

                <div className="flex items-center gap-fluid-2">
                  <label className={labelClass}>Lead time before appointment (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    value={dispatchSettings.appointmentLeadMinutes}
                    disabled={!dispatchSettings.checkinAutoCreateEnabled}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) =>
                      setDispatchSettings((prev) => ({
                        ...prev,
                        appointmentLeadMinutes: parseInt(e.target.value, 10) || 0,
                      }))
                    }
                    className={`${inputClass} w-[90px] ${
                      dispatchSettings.checkinAutoCreateEnabled
                        ? ""
                        : "bg-gray-100 dark:bg-acs-dark-hover text-gray-400 dark:text-acs-dark-subtext cursor-not-allowed"
                    }`}
                  />
                </div>

                <label
                  className={`flex items-start gap-fluid-1.5 text-fluid-sm ${
                    dispatchSettings.checkinAutoCreateEnabled
                      ? "text-gray-700 dark:text-acs-dark-subtext cursor-pointer"
                      : "text-gray-400 dark:text-acs-dark-subtext/50 cursor-not-allowed"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={dispatchSettings.autoAssignEnabled}
                    disabled={!dispatchSettings.checkinAutoCreateEnabled}
                    onChange={(e) =>
                      setDispatchSettings((prev) => ({ ...prev, autoAssignEnabled: e.target.checked }))
                    }
                    className="w-[15px] h-[15px] mt-fluid-0.25 accent-acs-checkbox dark:accent-acs-checkbox-dark disabled:cursor-not-allowed"
                  />
                  <span>
                    Auto-assign the job to an idle Hostler when it becomes due (falls back to the
                    unclaimed queue if none is available)
                  </span>
                </label>
              </div>
            </div>

            <hr className="border-none border-t border-gray-200 dark:border-acs-dark-border" />

            {/* ── Operating Hours + Holidays ── */}
            <div className="flex gap-fluid-4 items-start">
              {/* Operating Hours */}
              <div className="flex-1 max-w-[360px]">
                <p className={sectionLabelClass}>Operating Hours</p>
                <table className="border-collapse w-full">
                  <tbody>
                    {DAYS.map((day) => {
                      const isOpen = !!regularHours[day];
                      return (
                        <tr key={day}>
                          <td className="py-fluid-1 pr-fluid-2 text-fluid-sm text-gray-700 dark:text-acs-dark-subtext w-[100px]">
                            {day}
                          </td>
                          <td>
                            {isOpen ? (
                              <div className="flex items-center gap-fluid-1">
                                <input
                                  type="time"
                                  value={regularHours[day]?.from || ""}
                                  onChange={(e) => setHourField(day, "from", e.target.value)}
                                  className={`${inputClass} w-[115px]`}
                                />
                                <span className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext">–</span>
                                <input
                                  type="time"
                                  value={regularHours[day]?.to || ""}
                                  onChange={(e) => setHourField(day, "to", e.target.value)}
                                  className={`${inputClass} w-[115px]`}
                                />
                                <button
                                  onClick={() => toggleDay(day)}
                                  className="bg-transparent border-none cursor-pointer text-gray-400 dark:text-acs-dark-subtext
                                    text-fluid-base leading-none px-fluid-0.5"
                                  title="Mark closed"
                                >×</button>
                              </div>
                            ) : (
                              <span
                                onClick={() => toggleDay(day)}
                                className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext cursor-pointer underline"
                              >
                                Closed — click to open
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Holidays */}
              <div className="w-[240px] pt-fluid-0.5">
                <label className="flex items-center gap-fluid-1 text-fluid-sm text-gray-700 dark:text-acs-dark-subtext mb-fluid-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsWarehouse}
                    onChange={(e) => handleSameAsWarehouse(e.target.checked)}
                    className="w-[15px] h-[15px] accent-acs-checkbox dark:accent-acs-checkbox-dark"
                  />
                  Same as Warehouse
                </label>

                <p className={sectionLabelClass}>Holidays</p>
                <div className="flex gap-fluid-1 mb-fluid-2 items-center">
                  <DatezonePicker
                    date={holidayInput || new Date().toISOString().slice(0, 10)}
                    timezone={null}                          // hides the timezone portion
                    onDateChange={(date) => setHolidayInput(date)}
                    onTimezoneChange={() => {}}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = holidayInput.trim();
                      if (!val) return;
                      if (holidays.some((h) => h.date === val)) {
                        notify.error({ title: "Duplicate", message: "That date is already added." });
                        return;
                      }
                      setHolidays((prev) => [...prev, { date: val }]);
                      notify.success({ title: "Holiday Added", message: `${val} added as a holiday.` });
                      setHolidayInput("");
                    }}
                    disabled={!holidayInput}
                    className={`${modalPrimaryButtonClass} flex-shrink-0 ${holidayInput ? "" : "opacity-40 cursor-not-allowed"}`}
                  >+</button>
                </div>

                {/* Grouped by month */}
                {Object.entries(groupedHolidays).map(([month, hols]) => (
                  <CollapsibleMonthGroup
                    key={month}
                    month={month}
                    holidays={hols}
                    onRemove={removeHoliday}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <AddSectionModal
          onClose={() => setShowAddSectionModal(false)}
          onSave={handleAddSection}
        />
      )}
      {editingSection && (
        <EditSectionModal
          section={editingSection}
          onClose={() => setEditingSection(null)}
          onSave={handleEditSection}
        />
      )}
      {confirmDeleteId !== null && (
        <ConfirmDeleteModal
          onConfirm={() => { removeSection(confirmDeleteId); setConfirmDeleteId(null); }}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};

export default YardSettingsTab;
